import React, { ReactElement } from 'react';
import { StyleSheet } from 'react-native';
import { EmailTemplate, EmailTemplateStatus } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNAButton, DNACard, DNAText, Iffy } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  DNAChip,
  DNAEmpty,
  DNAEmailTemplatesContextMenu,
  EmptyVariant,
  useCurrentPage,
  TitleBar,
} from 'src/components/DNA';
import { EmailTemplateORM } from 'src/types/types';
import { useDispatch } from 'src/state/redux';
import { DRAWER_ENTITIES, drawerActions } from 'src/state/redux/slice/drawer';
import { useEmailTemplateList } from 'src/state/redux/selector/emailTemplate';
import { sortCollection } from 'src/state/redux/selector/common';
import emailTemplateQuery from 'src/state/redux/emailTemplate/query';

const styles = StyleSheet.create({
  rowMainWrapper: {
    minHeight: 'auto',
  },
  rowWrapper: {
    borderTopColor: colors['color-gray-80'],
    borderBottomColor: colors['color-gray-80'],
    borderTopWidth: 1,
    borderBottomWidth: 1,
    minHeight: 64,
    paddingHorizontal: 21,
    paddingVertical: 12,
  },
  header: {
    marginBottom: 16,
  },
  mainWrapper: {
    backgroundColor: colors['color-text-white'],
    minHeight: 'calc(100vh - 160px)',
    overflowX: 'auto',
  },
});

const EmailTemplates = () => {
  const emailTemplates = useEmailTemplateList();
  const orderedTemplates = sortCollection(emailTemplates, emailTemplateQuery.sorts.titleAsc);

  const dispatch = useDispatch();
  const currentPage = useCurrentPage()

  const onCreateEmailTemplate = (): void => {
    dispatch(drawerActions.toggle({
      entity: DRAWER_ENTITIES.EMAIL_TEMPLATE,
    }));
  };

  return (
    <DNABox style={styles.mainWrapper} appearance="col">
      { /* HEADER */}
      <DNABox>
        <TitleBar
          title={currentPage?.LABEL || ''}
          description="Create email templates for your audience"
        >
          <TitleBar.Button>
            <DNAButton
              testID="create-new-email-template-button"
              onPress={onCreateEmailTemplate}
            >
              Create new
            </DNAButton>
          </TitleBar.Button>
        </TitleBar>
      </DNABox>
      { /* TEMPLATES LIST */}
      <Iffy is={emailTemplates.length}>
        <DNABox testID="email-template-list">
          <EmailTemplateList emailTemplates={orderedTemplates} />
        </DNABox>
      </Iffy>
      { /* EMPTY STATE */}
      <Iffy is={!emailTemplates.length}>
        <DNACard testID="email-templates-list-empty" appearance="flat">
          <DNABox>
            <DNAEmpty
              emptyVariant={EmptyVariant.CustomLabelEmpty}
              customEmptyLabel="Create your first email template"
              customEmptyActionLabel="Create template"
              customEmptyOnPress={onCreateEmailTemplate}
            />
          </DNABox>
        </DNACard>
      </Iffy>
    </DNABox>
  );
};

function EmailTemplateList(props: { emailTemplates: EmailTemplateORM[] }): ReactElement {
  const dispatch = useDispatch()

  const openEmailTemplateEditorPanel = (emailTemplate: EmailTemplate) => {
    dispatch(drawerActions.toggle({
      entityId: emailTemplate.id,
      entity: DRAWER_ENTITIES.EMAIL_TEMPLATE,
    }))
    analytics?.track('EMAIL_EDIT', {
      action: 'EMAIL_EDIT',
      category: 'EMAIL',
      emailTemplateId: emailTemplate.id,
    });
  }

  return (
    <DNACard appearance="flat" >
      {
        props.emailTemplates.filter(x => x.model.status !== EmailTemplateStatus.REMOVED)
          .map((emailTemplateORM) => (
            <DNACard
              key={emailTemplateORM.model.id}
              appearance="flat"
              interactive="pressable"
              style={styles.rowMainWrapper}
              onPress={() => { openEmailTemplateEditorPanel(emailTemplateORM.model) }}
            >
              <DNABox
                testID="email-template-list"
                key={emailTemplateORM.model.id}
                alignY="center"
                style={styles.rowWrapper}
              >
                <DNABox style={{ marginRight: 24 }}>
                  <DNAEmailTemplatesContextMenu emailTemplateORM={emailTemplateORM}>
                    <DNAButton
                      appearance="ghost"
                      status="subtle"
                      iconLeft="dots-three-vertical-bold"
                    />
                  </DNAEmailTemplatesContextMenu>
                </DNABox>
                <DNABox appearance="col" fill>
                  <DNAText testID="email-template-list-name" numberOfLines={1}>{emailTemplateORM.model.title}</DNAText>
                  <DNABox spacing="xs" wrap="start" style={{ marginTop: 4 }}>
                    {
                      Object.values(emailTemplateORM.meta.customFilterValues).flat()?.map(({ displayValues }) => (
                        displayValues.map((value, idx) => (
                          <DNAChip.Beacon key={value + idx} children={value} />
                        ))
                      ))
                    }
                  </DNABox>
                </DNABox>
                <Iffy is={emailTemplateORM.model.status === EmailTemplateStatus.DRAFT}>
                  <DNAChip.Beacon variant="EMAIL_TEMPLATE_DRAFT" style={{ marginLeft: 24 }} />
                </Iffy>
                <Iffy is={emailTemplateORM.model.status === EmailTemplateStatus.DEACTIVATED}>
                  <DNAChip.Beacon variant="EMAIL_TEMPLATE_DEACTIVATED" style={{ marginLeft: 24 }} />
                </Iffy>
              </DNABox>
            </DNACard>
          ))
      }
    </DNACard>
  );
}

EmailTemplates.displayName = 'EmailTemplates';

export default EmailTemplates;
